package com.evoapps.evoshift.ui.map

import com.evoapps.evoshift.domain.model.GeoLocation
import kotlin.math.PI
import kotlin.math.atan
import kotlin.math.exp
import kotlin.math.floor
import kotlin.math.ln
import kotlin.math.pow
import kotlin.math.tan

/** A point on the map's world plane at some zoom, in pixels from the top-left corner (SH-132). */
data class WorldPoint(val x: Double, val y: Double)

/** One map tile: zoom, column and row, as in OpenStreetMap's `{z}/{x}/{y}.png`. */
data class TileKey(val zoom: Int, val x: Int, val y: Int)

/**
 * Web Mercator (EPSG:3857), the projection of OpenStreetMap's tiles: the world at zoom z is a
 * square of `tileSize * 2^z` pixels, split into `2^z × 2^z` tiles.
 */
object WebMercator {
    const val MIN_ZOOM = 2
    const val MAX_ZOOM = 19
    /** Mercator cannot show the poles; tiles stop here. */
    const val MAX_LATITUDE = 85.05112878

    fun tilesPerSide(zoom: Int): Int = 1 shl zoom

    fun worldSize(zoom: Int, tileSize: Double): Double = tileSize * 2.0.pow(zoom)

    fun toWorld(location: GeoLocation, zoom: Int, tileSize: Double): WorldPoint {
        val size = worldSize(zoom, tileSize)
        val latitude = location.latitude.coerceIn(-MAX_LATITUDE, MAX_LATITUDE) * PI / 180
        val x = (location.longitude + 180) / 360 * size
        val y = (1 - ln(tan(latitude) + 1 / kotlin.math.cos(latitude)) / PI) / 2 * size
        return WorldPoint(x, y)
    }

    /** The location at [point]; longitudes wrap around the world, latitudes stop at [MAX_LATITUDE]. */
    fun toLocation(point: WorldPoint, zoom: Int, tileSize: Double): GeoLocation {
        val size = worldSize(zoom, tileSize)
        val wrappedX = ((point.x % size) + size) % size
        val longitude = (wrappedX / size * 360 - 180).coerceIn(-180.0, 180.0)
        val n = PI - 2 * PI * point.y.coerceIn(0.0, size) / size
        val latitude = (180 / PI * atan(0.5 * (exp(n) - exp(-n)))).coerceIn(-MAX_LATITUDE, MAX_LATITUDE)
        return GeoLocation(latitude, longitude)
    }

    /**
     * The tiles covering a [width] × [height] view centred on [center], row by row from the
     * top-left, each with its top-left corner on the view. Columns wrap around (east of 180°
     * continues at -180°); rows beyond the poles are left out.
     */
    fun visibleTiles(center: GeoLocation, zoom: Int, tileSize: Double, width: Double, height: Double): List<PlacedTile> {
        val middle = toWorld(center, zoom, tileSize)
        val left = middle.x - width / 2
        val top = middle.y - height / 2
        val side = tilesPerSide(zoom)
        val firstColumn = floor(left / tileSize).toInt()
        val lastColumn = floor((left + width) / tileSize).toInt()
        val firstRow = floor(top / tileSize).toInt().coerceAtLeast(0)
        val lastRow = floor((top + height) / tileSize).toInt().coerceAtMost(side - 1)
        val tiles = mutableListOf<PlacedTile>()
        for (row in firstRow..lastRow) {
            for (column in firstColumn..lastColumn) {
                val wrapped = ((column % side) + side) % side
                tiles += PlacedTile(TileKey(zoom, wrapped, row), column * tileSize - left, row * tileSize - top)
            }
        }
        return tiles
    }

    /** Where [location] is on a [width] × [height] view centred on [center]. */
    fun toView(location: GeoLocation, center: GeoLocation, zoom: Int, tileSize: Double, width: Double, height: Double): WorldPoint {
        val point = toWorld(location, zoom, tileSize)
        val middle = toWorld(center, zoom, tileSize)
        return WorldPoint(point.x - middle.x + width / 2, point.y - middle.y + height / 2)
    }

    /** The location at ([x], [y]) on a [width] × [height] view centred on [center]. */
    fun fromView(x: Double, y: Double, center: GeoLocation, zoom: Int, tileSize: Double, width: Double, height: Double): GeoLocation {
        val middle = toWorld(center, zoom, tileSize)
        return toLocation(WorldPoint(middle.x + x - width / 2, middle.y + y - height / 2), zoom, tileSize)
    }

    /** [center] moved by ([dx], [dy]) pixels of the view (a drag moves the map the other way). */
    fun shift(center: GeoLocation, dx: Double, dy: Double, zoom: Int, tileSize: Double): GeoLocation {
        val middle = toWorld(center, zoom, tileSize)
        return toLocation(WorldPoint(middle.x + dx, middle.y + dy), zoom, tileSize)
    }
}

/** A tile and where its top-left corner falls on the view, in pixels. */
data class PlacedTile(val key: TileKey, val left: Double, val top: Double)
